import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiError, getCourse } from "../../services/courseApi";
import {
  createSessionContent,
  deleteSessionContent,
  listSessionContent,
  updateSessionContent,
} from "../../services/sessionContentApi";
import {
  emptyContentForm,
  fromContent,
  toContentPayload,
} from "./sessionContentUtils";
import "./course.css";

function findSession(course, sessionId) {
  for (const topic of course?.topics || []) {
    for (const subtopic of topic.subtopics || []) {
      const session = (subtopic.sessions || []).find(
        (item) => Number(item.curriculum_session_id) === Number(sessionId)
      );
      if (session) return { session, topic, subtopic };
    }
  }
  return null;
}

function MediaLinks({ note }) {
  const media = note.media || {};
  const resources = [
    ...(note.video_link ? [{ label: "Video", value: note.video_link, icon: "bi-play-circle" }] : []),
    ...(media.audio || []).map((item, index) => ({ label: `Audio ${index + 1}`, value: item.audio_link, icon: "bi-volume-up" })),
    ...(media.images || []).map((item, index) => ({ label: `Image ${index + 1}`, value: item.image_link, icon: "bi-image" })),
    ...(media.links || []).map((item, index) => ({ label: item.description || `Resource ${index + 1}`, value: item.value, icon: "bi-link-45deg" })),
  ];
  if (!resources.length) return null;
  return (
    <div className="session-content-resources">
      {resources.map((resource, index) => (
        <a key={`${resource.value}-${index}`} href={resource.value} target="_blank" rel="noreferrer">
          <i className={`bi ${resource.icon}`} />{resource.label}
        </a>
      ))}
    </div>
  );
}

export default function SessionContent() {
  const { courseId, sessionId } = useParams();
  const [course, setCourse] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyContentForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const context = useMemo(() => findSession(course, sessionId), [course, sessionId]);

  async function load() {
    setLoading(true);
    try {
      const [courseResult, contentResult] = await Promise.all([
        getCourse(courseId),
        listSessionContent(sessionId),
      ]);
      setCourse(courseResult);
      setItems(contentResult);
    } catch (error) {
      toast.error(getApiError(error, "Could not load session content"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, sessionId]);

  function resetForm() {
    setEditingId(null);
    setForm(emptyContentForm);
  }

  function edit(note) {
    setEditingId(note.id);
    setForm(fromContent(note));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function submit(event) {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error("Content title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = toContentPayload(form);
      if (editingId) {
        await updateSessionContent(editingId, sessionId, payload);
        toast.success("Session content updated");
      } else {
        await createSessionContent(sessionId, payload);
        toast.success("Session content added");
      }
      resetForm();
      await load();
    } catch (error) {
      toast.error(getApiError(error, "Could not save session content"));
    } finally {
      setSaving(false);
    }
  }

  async function remove(note) {
    if (!window.confirm(`Delete “${note.title}”?`)) return;
    try {
      await deleteSessionContent(note.id);
      toast.success("Session content deleted");
      if (editingId === note.id) resetForm();
      await load();
    } catch (error) {
      toast.error(getApiError(error, "Could not delete session content"));
    }
  }

  if (loading) return <div className="course-state"><div className="spinner-border text-primary" /></div>;

  return (
    <main className="course-page">
      <Link className="course-back-link" to={`/courses/${courseId}`}>
        <i className="bi bi-arrow-left me-2" />Back to course
      </Link>

      <div className="course-page-header mt-3">
        <div>
          <p className="course-eyebrow">Curriculum session content</p>
          <h1>{context?.session?.title || `Session #${sessionId}`}</h1>
          <p className="text-muted mb-0">
            {[course?.title, context?.topic.title, context?.subtopic.title].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>

      <section className="course-panel">
        <div className="course-section-heading">
          <div>
            <h2>{editingId ? "Edit content" : "Add content"}</h2>
            <p className="text-muted mb-0">Attach reusable text, video, audio, images, or reference links.</p>
          </div>
          {editingId && <button className="btn btn-sm btn-outline-secondary" type="button" onClick={resetForm}>Cancel edit</button>}
        </div>
        <form className="session-content-form mt-4" onSubmit={submit}>
          <label className="course-form-field session-content-wide">
            <span>Title <em>*</em></span>
            <input className="form-control" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="e.g. Open chord reference" />
          </label>
          <label className="course-form-field session-content-wide">
            <span>Description</span>
            <textarea className="form-control" rows="4" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Learning material or instructions" />
          </label>
          <label className="course-form-field">
            <span>Video URL</span>
            <input className="form-control" type="url" value={form.video_link} onChange={(event) => setForm({ ...form, video_link: event.target.value })} placeholder="https://…" />
          </label>
          <label className="course-form-field">
            <span>Audio URLs</span>
            <textarea className="form-control" rows="3" value={form.audio_links} onChange={(event) => setForm({ ...form, audio_links: event.target.value })} placeholder="One URL per line" />
          </label>
          <label className="course-form-field">
            <span>Image URLs</span>
            <textarea className="form-control" rows="3" value={form.image_links} onChange={(event) => setForm({ ...form, image_links: event.target.value })} placeholder="One URL per line" />
          </label>
          <label className="course-form-field">
            <span>Resource links</span>
            <textarea className="form-control" rows="3" value={form.resource_links} onChange={(event) => setForm({ ...form, resource_links: event.target.value })} placeholder="Label | https://… (one per line)" />
          </label>
          <div className="session-content-actions session-content-wide">
            <button className="btn btn-primary" disabled={saving} type="submit">
              {saving ? "Saving…" : editingId ? "Update content" : "Add content"}
            </button>
          </div>
        </form>
      </section>

      <section className="course-panel">
        <div className="course-section-heading">
          <div><h2>Session content</h2><p className="text-muted mb-0">{items.length} item{items.length === 1 ? "" : "s"}</p></div>
        </div>
        {!items.length ? (
          <div className="session-content-empty"><i className="bi bi-journal-plus" /><strong>No content added yet</strong><span>Add the first learning resource using the form above.</span></div>
        ) : (
          <div className="session-content-list">
            {items.map((note) => (
              <article className="session-content-card" key={note.id}>
                <div className="session-content-card-heading">
                  <div><span className="course-session-icon"><i className="bi bi-journal-text" /></span><h3>{note.title}</h3></div>
                  <div className="d-flex gap-2"><button className="btn btn-sm btn-outline-primary" onClick={() => edit(note)}>Edit</button><button className="btn btn-sm btn-outline-danger" onClick={() => remove(note)}>Delete</button></div>
                </div>
                {note.description && <p>{note.description}</p>}
                <MediaLinks note={note} />
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
